import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { IAccountSliceState } from "../utils/account.types";
import { ApiStatus } from "../utils/api.types";
import {
    addAccount,
    deleteAccount,
    fetchAccounts,
    updateAccount,
    removeAccountLogoOrSign,
    fetchAccountById,
    updateLegalData,
    updateAccountingData,
    addBuyerSellerAccount,
    updateBuyerSellerAccount
} from "./accountThunk";

/**
 * Account slice initial state.
 */
const initialState: IAccountSliceState = {
    accounts: [],
    status: ApiStatus.IDLE,
    errorMessage: '',
    isAccountCreated: false,
    accountDetails: {},
    selectedAccountId: ''
}

/**
 * Account Slice.
 */
const accountSlice = createSlice({
    name: 'account',
    initialState: initialState,
    reducers: {
        // Selected accoun id
        selectedAccountID: (state, action) => {
            state.selectedAccountId = action.payload || '';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Accounts
            .addCase(fetchAccounts.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAccounts.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.accounts = action.payload.accounts || [];
                state.errorMessage = '';
            })
            .addCase(fetchAccounts.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Legal data
            .addCase(updateLegalData.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateLegalData.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, account: updatedAccount } = action.payload
                state.accountDetails = { ...updatedAccount };
                state.accounts = state.accounts
                    .map(account => (account._id === updatedAccount._id ? { ...account, ...updatedAccount } : account));
                state.errorMessage = '';
                toast.success(message);
            })
            .addCase(updateLegalData.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Accounting data
            .addCase(updateAccountingData.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateAccountingData.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, account: updatedAccount } = action.payload
                state.accountDetails = { ...updatedAccount };
                state.accounts = state.accounts
                    .map(account => (account._id === updatedAccount._id ? { ...account, ...updatedAccount } : account));
                state.errorMessage = '';
                toast.success(message);
            })
            .addCase(updateAccountingData.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Fetch Accounts by ID
            .addCase(fetchAccountById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAccountById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.accountDetails = action.payload || [];
                state.errorMessage = '';
            })
            .addCase(fetchAccountById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Add Account
            .addCase(addAccount.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addAccount.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.isAccountCreated = true;
                state.accounts.push(action.payload.account);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addAccount.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isAccountCreated = false;
                state.errorMessage = action.error.message
            })
            // Delete Account
            .addCase(deleteAccount.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteAccount.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.accounts = state.accounts.filter(account => account._id !== id)

                toast.success(message);
            })
            .addCase(deleteAccount.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })
            // Update Account
            .addCase(updateAccount.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateAccount.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, account: updatedAccount } = action.payload
                state.accounts = state.accounts
                    .map(account => (account._id === updatedAccount._id ? { ...account, ...updatedAccount } : account));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateAccount.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })

            // Update Account for removeAccountLogoOrSign
            .addCase(removeAccountLogoOrSign.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(removeAccountLogoOrSign.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, account: updatedAccount } = action.payload
                state.accounts = state.accounts
                    .map(account => (account._id === updatedAccount._id ? { ...account, ...updatedAccount } : account));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(removeAccountLogoOrSign.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
            
            // Add create/buyer Account
            .addCase(addBuyerSellerAccount.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addBuyerSellerAccount.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                // toast.success(message);
            })
            .addCase(addBuyerSellerAccount.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })

            // Add update buyer Account
            .addCase(updateBuyerSellerAccount.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateBuyerSellerAccount.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(updateBuyerSellerAccount.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
                toast.error(state.errorMessage);
            })
    }
});

// export const {  } = accountSlice.actions;

export default accountSlice.reducer;